import { useMemo } from 'react';
import {
  useDatabaseRange,
  useFlattenedGeometryShapes,
  usePortsGetList,
  useShapesList,
} from '@ais-ui/api';
import { useGetFilters, useGetShareGroups } from '@ais-ui/websockets';
import type {
  ShapeCollection,
  PortCollection,
  FilterCollection,
} from '../../utils/formValueBuilders';
import type { ReportType } from '../../../reportsTypes';

interface ReportFormResources {
  shapes: ShapeCollection;
  filters: FilterCollection | undefined;
  ports: PortCollection;
  databaseRange: ReturnType<typeof useDatabaseRange>['data'];
  isLoading: boolean;
  isLoadingShareGroups: boolean;
  isLoadingDatabaseRange: boolean;
}

interface UseReportFormResourcesParams {
  reportType?: ReportType;
}

export function useReportFormResources(
  params?: UseReportFormResourcesParams,
): ReportFormResources {
  const { reportType } = params || {};

  const { data: shapesResponse, isLoading: isLoadingShapes } =
    useFlattenedGeometryShapes();
  const { data: rawShapesResponse } = useShapesList();
  const { data: filtersResponse, isLoading: isLoadingFilters } =
    useGetFilters();
  const { data: portsResponse, isLoading: isLoadingPorts } = usePortsGetList();
  const { isLoading: isLoadingShareGroups } = useGetShareGroups();
  const { data: databaseRange, isLoading: isDatabaseRangeLoading } =
    useDatabaseRange();

  // Include both flattened features AND top-level feature collections
  // so createShapeDictionary can find FCs by ID
  const shapes = useMemo<ShapeCollection>(() => {
    const flattened = shapesResponse ?? [];
    const raw = rawShapesResponse?.records ?? [];
    const featureCollections = raw.filter(
      (s) => s.type === 'FeatureCollection',
    );
    return [...flattened, ...featureCollections];
  }, [shapesResponse, rawShapesResponse]);

  const filters = useMemo<FilterCollection | undefined>(() => {
    if (!filtersResponse) return undefined;
    return filtersResponse;
  }, [filtersResponse]);

  const ports = useMemo<PortCollection>(() => {
    return portsResponse?.records ?? [];
  }, [portsResponse]);

  // Check if the current report type requires ports
  const requiresPorts = useMemo(() => {
    return reportType?.shape?.ports !== undefined;
  }, [reportType]);

  const isLoading = useMemo(() => {
    // Shapes and filters should always load successfully
    const criticalResourcesLoading = isLoadingShapes || isLoadingFilters;

    // Only include ports loading if the report type actually uses ports
    // Some customers don't have access to ports, so we shouldn't block on it
    // unless it's actually required (e.g., PORT_CALLS report)
    const portsLoading = requiresPorts ? isLoadingPorts : false;

    return criticalResourcesLoading || portsLoading;
    // Note: isDatabaseRangeLoading is intentionally excluded - this endpoint is not critical
    // and should not block the form from loading (it can return 503 errors)
  }, [isLoadingShapes, isLoadingFilters, isLoadingPorts, requiresPorts]);

  return {
    shapes,
    filters,
    ports,
    databaseRange,
    isLoading,
    isLoadingShareGroups,
    isLoadingDatabaseRange: isDatabaseRangeLoading,
  };
}
